'use strict';

import { mapGetters, mapMutations, mapState } from 'vuex';
import { supportedLocales } from '../i18n';
import { getAuthOrgId } from '../store/modules/auth';
import { getLocaleTimestamp } from './utils';

export default {
  metaInfo() {
    const routerPageTitle = this.$route?.meta?.label
      ? this.$maybeT(this.$route.meta.label)
      : undefined;

    return {
      title: routerPageTitle,
    };
  },
  data() {
    return {
      locationResolverUrl: process.env.VUE_APP_LOCATION_RESOLVER_URL,
    };
  },
  computed: {
    ...mapGetters(['defaultCountry']),
    ...mapGetters('auth', [
      'isGlobalUser',
      'isOrgUser',
      'isOrgAdmin',
      'getAuthOrg',
      'getAuthUser',
      'getAuthProfile',
      'belongsToFinancialMinistry',
      'GET_IMPERSONATION_SESSION',
      'getAuthOrgCountry',
      'isAuthOrgVerified',
    ]),
    ...mapGetters('organisations/organisation_preferences', ['GET_ALL_ORGANISATION_PREFERENCES']),
    ...mapState({
      isProduction: (state) => state.isProduction,
      inDevelopment: (state) => state.inDevelopment,
      inStagingMode: (state) => state.inStagingMode,
    }),

    isImpersonationSessionActive() {
      return !!this.GET_IMPERSONATION_SESSION?.active;
    },

    resolvedOrgId() {
      /** @type {string | undefined} */
      let churchId =
        this.$route.params.churchId ||
        (this.isGlobalUser && this.GET_IMPERSONATION_SESSION.active
          ? this.GET_IMPERSONATION_SESSION.churchId
          : getAuthOrgId() ?? this.getAuthOrg?.id);

      if (churchId === 'undefined') {
        churchId = undefined;
      }

      return churchId;
    },

    is1024ScreenOrBelow() {
      return this.$q.screen.lt.md || this.$q.screen.width === this.$q.screen.sizes.md;
    },

    currentLocaleObject() {
      return supportedLocales.find((locale) => locale.code === this.$i18next.language) ?? {};
    },
  },

  methods: {
    ...mapMutations(['setTopActionsOptions', 'setSecondaryBackButtonTo', 'setDefaultCountry']),
    ...mapMutations('auth', ['setImpersonationSession']),
    /**
     * @param {string=} key
     */
    $maybeT(key, options) {
      if (!key) {
        return '';
      }

      if (typeof key !== 'string') {
        return key;
      }

      const prefix = '$t__';

      if (!key.startsWith(prefix)) {
        return key;
      }
      const translationKey = key.replace(prefix, '');
      const [namespace, ...keys] = translationKey.split('.');
      if (!keys.length) {
        throw new Error('Translation key is needed after the namespace');
      }

      // CRITICAL: Never use `i18next.t` directly here. It causes loss of reactivity.
      return this.$t(keys.join('.'), { ns: namespace, ...options });
    },
    $gt(key, options) {
      let componentDefaultNamespace = undefined;
      const isNsSet =
        typeof options === 'object' && typeof options.ns === 'string' && !!options['ns'];
      if ('i18nOptions' in this.$options) {
        if (this.$options.i18nOptions.defaultNamespace) {
          componentDefaultNamespace = this.$options.i18nOptions.defaultNamespace;
        }
      }
      // CRITICAL: Never use `i18next.t` directly here. It causes loss of reactivity.
      return this.$t(key, { ...options, ns: isNsSet ? options.ns : componentDefaultNamespace });
    },
    /**
     *
     * @param {Record<string, string | null | undefined>} obj
     */
    translateObj(obj) {
      const self = this;
      return Object.entries(obj).reduce((prev, [key, value]) => {
        if (typeof value === 'string') {
          prev[key] = self.$maybeT(value);
        } else {
          prev[key] = value;
        }
        return prev;
      }, {});
    },

    async resolveDefaultCountry() {
      /**
       * !!Critical: Don't call this method in this mixin's
       * created or mounted hooks. It leads to the method being called
       * for a large number of times.
       *
       * It is already called in the `AdminAreaLayout` component once.
       */
      if (!this.locationResolverUrl) {
        return;
      }

      const country = await fetch(this.locationResolverUrl)
        .then((response) => {
          if (response.ok) {
            return response.json();
          }
          return '';
        })
        .then((json) => {
          if (json === '') {
            return json;
          }

          if (!json.country) {
            return '';
          }

          return json.country;
        })
        .catch((error) => {
          console.error('Error resolving default country: %o', error);
          return '';
        });

      this.setDefaultCountry(country);
    },
    getLocaleTimestamp(timestamp) {
      return getLocaleTimestamp(timestamp);
    },
  },
};
